import { memo, useCallback, useMemo } from 'react';

import { Button, CopyText, Flex, Heading, Label, Switch, Text, TruncatedText } from '@/components/core';
import { ActionRunTest, HowToGuide } from '@/components/flow';
import { WarningDuotoneIcon } from '@/components/icons';
import { PowerAutomateLogo } from '@/components/logos';
import { useGetAppClientWithSecret } from '@/hooks';
import { useTriggerStore } from '@/store';
import { isNotConfiguredExport } from '@/utils';

export type ExportPowerAutomateConfigurationProps = object;

// eslint-disable-next-line no-empty-pattern
const IExportPowerAutomateConfiguration = ({}: ExportPowerAutomateConfigurationProps = {}) => {
  const action = useTriggerStore((state) => state.action);
  const updateAction = useTriggerStore((state) => state.updateAction);

  const updateWaitForResult = useCallback(
    (waitForResult: boolean) => {
      updateAction({ config: { ...action.config, waitForResult } });
    },
    [action.config, updateAction]
  );

  const { data: appClient } = useGetAppClientWithSecret();

  const clientCredentials = useMemo(
    () => (appClient?.clientSecret ? btoa(`${appClient.clientId}:${appClient.clientSecret}`) : undefined),
    [appClient]
  );

  const isConfigured = useMemo(() => !isNotConfiguredExport(action), [action]);

  return (
    <Flex col className="gap-md p-md grow">
      <Flex className="p-sm gap-sm bg-gray-25 items-center rounded-md border-1 border-gray-100">
        <PowerAutomateLogo className="shrink-0" size="xl" />

        <TruncatedText className="grow-0">Power Automate</TruncatedText>

        <div className="grow" />

        <Button
          className="shrink-0"
          variant="primary"
          onPress={() => window.open('https://make.powerautomate.com', '_blank')}
        >
          <Text>Open</Text>
        </Button>
      </Flex>

      <Flex col className="gap-xs">
        <Label>Enabled</Label>
        <Switch
          isSelected={(action.enabled as boolean | undefined) ?? false}
          onChange={(isSelected) => updateAction({ enabled: isSelected })}
        />
      </Flex>

      <Flex col className="gap-xs">
        <Label>Wait for result</Label>
        <Switch
          isSelected={(action.config.waitForResult as boolean | undefined) ?? false}
          onChange={(isSelected) => updateWaitForResult(isSelected)}
        />
      </Flex>

      <Flex col className="gap-xs">
        <Label>Client Credentials</Label>
        <CopyText isSensitive value={clientCredentials} />
      </Flex>

      {!isConfigured ? (
        <Flex variant="warning" className="p-md gap-sm rounded-lg">
          <WarningDuotoneIcon />

          <Flex col className="border-none">
            <Heading size="sm">Setup required</Heading>
            <Text>Make sure your Power Automate flow is saved and configured with Cradl AI.</Text>
          </Flex>
        </Flex>
      ) : null}

      {isConfigured && <ActionRunTest label="Test connection" />}

      <div className="grow" />

      <HowToGuide actionId={action?.actionId} />
    </Flex>
  );
};

export const ExportPowerAutomateConfiguration = memo(
  IExportPowerAutomateConfiguration
) as typeof IExportPowerAutomateConfiguration;
